import { useMutation } from '@tanstack/react-query';
import { ApiError, importEndpoints, photoBundleUpload } from '@ooh/contracts';
import type { AmbiguousSupplierEntry } from '@ooh/contracts';
import type { ApiClient } from '../../plugs/api-client';
import { buildPhotoZip } from '../lib/upload-file-selection';

export interface UploadPhaseOnNextArgs {
  jobId: number;
  requiresMapping: boolean;
  isPhotoBundle?: boolean;
  ambiguousSuppliers?: AmbiguousSupplierEntry[];
}

/** The server's `error` / `message` for a rejected upload; other failures keep their own message. */
function uploadErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { error?: string; message?: string };
    return body.error ?? body.message ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

export function useUploadMutations(
  api: ApiClient,
  errorLabel: string,
  onNext: (args: UploadPhaseOnNextArgs) => void,
  onError: (msg: string) => void,
) {
  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.upload(importEndpoints.upload, file),
    onSuccess: (data) => {
      onNext({
        jobId: data.importJobId,
        requiresMapping: data.requiresMapping,
        ambiguousSuppliers: data.ambiguousSuppliers ?? [],
      });
    },
    onError: (err: unknown) => onError(uploadErrorMessage(err, errorLabel)),
  });

  const photoBundleMutation = useMutation({
    mutationFn: async ({ file, supplierId }: { file: File | File[]; supplierId: number }) => {
      let zipFile: File;
      if (Array.isArray(file)) {
        try {
          zipFile = await buildPhotoZip(file);
        } catch (err) {
          // Client-side ZIP packaging failed (e.g. dynamic client-zip import or blob() rejected).
          // Fall back to the same localized errorLabel used for HTTP failures below, rather than
          // surfacing a raw, untranslated Error.message to the user.
          console.error('[photo-bundle] client-side zip build failed', err);
          throw new Error(errorLabel);
        }
      } else {
        zipFile = file;
      }
      return api.upload(photoBundleUpload, zipFile, { supplierId: String(supplierId) });
    },
    onSuccess: (data) => {
      onNext({ jobId: data.importJobId, requiresMapping: false, isPhotoBundle: true });
    },
    onError: (err: unknown) => onError(uploadErrorMessage(err, errorLabel)),
  });

  return { uploadMutation, photoBundleMutation };
}
